import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteConversation } from "@/hooks/useDeleteConversation";
import { RenameConversationDialog } from "./rename-conversation-dialog";
import { cn } from "@/utils/cn";
import { MoreVertical, PencilIcon, Trash2 } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material";
import { useStoredTheme } from "@/utils/theme-context";
import { useTranslation } from "react-i18next";

export const PlaygroundListItem = ({
  title,
  id,
  active,
}: {
  title: string;
  id: string;
  active?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const storedTheme = useStoredTheme();
  const theme = useTheme();
  const location = useLocation();
  const path = location.pathname.split("/");

  const { mutate } = useDeleteConversation(id, path[2]);
  return (
    <div
      className={cn(
        "flex items-center justify-between py-1 px-2 text-[var(--text-color)]  hover:bg-[var(--hover-bg)] cursor-pointer rounded-[8px]",
        {
          "bg-[var(--active-bg)] font-semibold": active,
        },
      )}
      style={
        {
          "--text-color": storedTheme.sidebar.textColor[theme.palette.mode],
          "--active-bg":
            storedTheme.sidebar.activeLinkBackgroundColor[theme.palette.mode],
          "--hover-bg":
            storedTheme.sidebar.hoverBackgroundColor[theme.palette.mode],
        } as React.CSSProperties
      }
    >
      <Link className="w-[90%]" to={`/${path[1]}/${id}`}>
        <span className="truncate w-[90%] flex text-foreground text-[14px]">
          {title}
        </span>
      </Link>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <MoreVertical className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="bg-[var(--card-background)] border-[1px] border-solid border-ring"
          align={isRTL ? "end" : "start"}
        >
          <RenameConversationDialog
            conversationId={id}
            currentName={title}
            trigger={
              <DropdownMenuItem
                className="cursor-pointer"
                onSelect={(event) => event.preventDefault()}
                dir={isRTL ? "rtl" : "ltr"}
              >
                <PencilIcon className="mr-2 rtl:mr-0 rtl:ml-2 h-4 w-4" />
                <span>{t("common.rename")}</span>
              </DropdownMenuItem>
            }
          />
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <DropdownMenuItem
                className="text-red-500 focus:text-red-500 cursor-pointer"
                onSelect={(event) => event.preventDefault()}
                dir={isRTL ? "rtl" : "ltr"}
              >
                <Trash2 className="text-red-500 mr-2 rtl:mr-0 rtl:ml-2 h-4 w-4" />
                <span>{t("common.delete")}</span>
              </DropdownMenuItem>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[var(--card-background)] border-[1px] border-solid border-ring">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t("playground.deleteConversationTitle")}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t("playground.deleteConversationDescription")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="py-3 px-6 rounded-xs bg-transparent">
                  {t("common.cancel")}
                </AlertDialogCancel>
                <AlertDialogAction
                  className="py-3 px-6 rounded-xs text-red-500 bg-transparent hover:bg-red-400/20"
                  onClick={() => mutate()}
                >
                  {t("common.delete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
